<template>
  <form>
    <RiskAssessmentForm
      :name="network.name"
      :type="network.type"
      :sections="displayedSections"
      :interventions="displayedInterventions"
      :suggestions="displayedSuggestions"
      :editable="editable"
      @check="mutableSections = $event"
    />
  </form>
</template>

<script>
import RiskAssessmentForm from '@/components/sites/riskAssessments/RiskAssessmentForm.vue';

export default {
    name: 'RiskAssessmentClosedNetworkForm',
    components: {
        RiskAssessmentForm,
    },
    props: {
        sections: {
            type: Array,
            default: () => [],
        },
        editable: {
            type: Boolean,
            default: true,
        },
    },
    data() {
        return {
            interventionsRules: [
                {
                    rule: 2,
                    conditions: [
                        { key: 'F8', value: true },
                        { key: 'F9', value: true },
                        { key: 'F25', value: false },
                    ],
                    risk: this.$t('riskAnalysis.closedNetwork.interventions.softeningRequired'),
                },
                {
                    rule: 4,
                    conditions: [
                        { key: 'F4', value: true },
                        { key: 'F5', value: true },
                        { key: 'F7', value: true },
                        { key: 'F13', value: false },
                        { key: 'F14', value: false },
                    ],
                    risk: this.$t('riskAnalysis.closedNetwork.interventions.filtratonOptimization'),
                },
                {
                    rule: 3,
                    conditions: [
                        { key: 'F22', value: true },
                        { key: 'F23', value: false },
                        { key: 'F24', value: false },
                        { key: 'F6', value: false },
                    ],
                    risk: this.$t('riskAnalysis.closedNetwork.interventions.filtrationDisinfection'),
                },
            ],
            displayedSections: [
                {
                    name: this.$t('riskAnalysis.physicalInspection'),
                    fields: [
                        {
                            key: 'F1',
                            label: this.$t('riskAnalysis.closedNetwork.tubeHeatExchangers'),
                            yesValue: 0,
                            noValue: 0,
                            value: null,
                        },
                        {
                            key: 'F2',
                            label: this.$t('riskAnalysis.closedNetwork.frameHeatExchangers'),
                            yesValue: 1,
                            noValue: 0,
                            value: null,
                        },
                        {
                            key: 'F3',
                            label: this.$t('riskAnalysis.closedNetwork.componentWithWater'),
                            yesValue: 2,
                            noValue: 0,
                            value: null,
                        },
                        {
                            key: 'F4',
                            label: this.$t('riskAnalysis.closedNetwork.depositAccumulation'),
                            yesValue: 3,
                            noValue: 0,
                            value: null,
                        },
                        {
                            key: 'F5',
                            label: this.$t('riskAnalysis.closedNetwork.activeCorrosion'),
                            yesValue: 3,
                            noValue: 0,
                            value: null,
                        },
                        {
                            key: 'F6',
                            label: this.$t('riskAnalysis.closedNetwork.odor'),
                            yesValue: 3,
                            noValue: 0,
                            value: null,
                        },
                        {
                            key: 'F7',
                            label: this.$t('riskAnalysis.closedNetwork.turbidWater'),
                            yesValue: 3,
                            noValue: 0,
                            value: null,
                        },
                        {
                            key: 'F8',
                            label: this.$t('riskAnalysis.closedNetwork.waterLoss'),
                            yesValue: 3,
                            noValue: 0,
                            value: null,
                        },
                        {
                            key: 'F9',
                            label: this.$t('riskAnalysis.closedNetwork.operatingTemperature'),
                            yesValue: 3,
                            noValue: 0,
                            value: null,
                        },
                    ],
                },
                {
                    name: this.$t('riskAnalysis.applicationConditions'),
                    fields: [
                        {
                            key: 'F10',
                            label: this.$t('riskAnalysis.closedNetwork.corrosionInhibitor'),
                            yesValue: 0,
                            noValue: 3,
                            value: null,
                        },
                        {
                            key: 'F11',
                            label: this.$t('riskAnalysis.closedNetwork.oxygenScavenger'),
                            yesValue: 2,
                            noValue: 0,
                            value: null,
                        },
                        {
                            key: 'F12',
                            label: this.$t('riskAnalysis.closedNetwork.automaticInjection'),
                            yesValue: 0,
                            noValue: 1,
                            value: null,
                        },
                        {
                            key: 'F13',
                            label: this.$t('riskAnalysis.closedNetwork.sideStreamFiltration'),
                            yesValue: 0,
                            noValue: 2,
                            value: null,
                        },
                        {
                            key: 'F14',
                            label: this.$t('riskAnalysis.closedNetwork.filtrationDown'),
                            yesValue: 0,
                            noValue: 2,
                            value: null,
                        },
                        {
                            key: 'F15',
                            label: this.$t('riskAnalysis.closedNetwork.coilsLayUp'),
                            yesValue: 3,
                            noValue: 0,
                            value: null,
                        },
                        {
                            key: 'F16',
                            label: this.$t('riskAnalysis.closedNetwork.stagnation'),
                            yesValue: 1,
                            noValue: 0,
                            value: null,
                        },
                        {
                            key: 'F17',
                            label: this.$t('riskAnalysis.closedNetwork.weeklyTesting'),
                            yesValue: 0,
                            noValue: 2,
                            value: null,
                        },
                        {
                            key: 'F18',
                            label: this.$t('riskAnalysis.closedNetwork.corrosionCoupons'),
                            yesValue: 0,
                            noValue: 1,
                            value: null,
                        },
                        {
                            key: 'F19',
                            label: this.$t('riskAnalysis.closedNetwork.manualAvailable'),
                            yesValue: 0,
                            noValue: 1,
                            value: null,
                        },
                        {
                            key: 'F20',
                            label: this.$t('riskAnalysis.closedNetwork.safetyEquipmentOnSite'),
                            yesValue: 0,
                            noValue: 1,
                            value: null,
                        },
                    ],
                },
                {
                    name: this.$t('riskAnalysis.analyticalResults'),
                    fields: [
                        {
                            key: 'F21',
                            label: this.$t('riskAnalysis.closedNetwork.minimalConcentration'),
                            yesValue: 0,
                            noValue: 2,
                            value: null,
                        },
                        {
                            key: 'F22',
                            label: this.$t('riskAnalysis.closedNetwork.sulfateBacteriaUndetected'),
                            yesValue: 0,
                            noValue: 3,
                            value: null,
                        },
                        {
                            key: 'F23',
                            label: this.$t('riskAnalysis.closedNetwork.fermentingBacteriaUndetected'),
                            yesValue: 0,
                            noValue: 1,
                            value: null,
                        },
                        {
                            key: 'F24',
                            label: this.$t('riskAnalysis.closedNetwork.highPh'),
                            yesValue: 0,
                            noValue: 2,
                            value: null,
                        },
                        {
                            key: 'F25',
                            label: this.$t('riskAnalysis.closedNetwork.waterHardness'),
                            yesValue: 0,
                            noValue: 2,
                            value: null,
                        },
                    ],
                },
            ],
            mutableSections: this.sections,
            network: {
                name: null,
                type: null,
            },
            riskAnalysis: {
                network: null,
                fields: [],
            },
        };
    },
    computed: {
        displayedSuggestions() {
            const displayedSuggestions = [];

            if (this.results('F3')) {
                displayedSuggestions.push(this.$t('riskAnalysis.closedNetwork.suggestions.useProductOnAluminum'));
            }
            if (this.results('F6') && this.results('F7')) {
                displayedSuggestions.push(this.$t('riskAnalysis.closedNetwork.suggestions.biocideDisinfection'));
            }
            if (this.results('F7')) {
                displayedSuggestions.push(this.$t('riskAnalysis.closedNetwork.suggestions.bypassFiltration'));
            }
            if (this.results('F8')) {
                displayedSuggestions.push(this.$t('riskAnalysis.closedNetwork.suggestions.correctLeaks'));
            }
            if (this.results('F13') === false && this.results('F14') === false) {
                displayedSuggestions.push(this.$t('riskAnalysis.closedNetwork.suggestions.filtratonOptimization'));
            }
            if (this.results('F15')) {
                displayedSuggestions.push(this.$t('riskAnalysis.closedNetwork.suggestions.glycolReplacement'));
            }
            if (this.results('F17') === false && this.results('F12') === false) {
                displayedSuggestions.push(this.$t('riskAnalysis.closedNetwork.suggestions.increaseControls'));
            }
            if (this.results('F20') === false) {
                displayedSuggestions.push(this.$t('riskAnalysis.closedNetwork.suggestions.aquireEquipment'));
            }
            if (this.results('F22') === false && this.results('F23') === false) {
                displayedSuggestions.push(this.$t('riskAnalysis.closedNetwork.suggestions.desinfectionControl'));
            }

            return displayedSuggestions;
        },
        displayedInterventions() {
            const displayedInterventions = [];

            this.interventionsRules.forEach(rule => {
                let t = 0;

                rule.conditions.forEach(condition => {
                    if (this.results(condition.key) === condition.value) t += 1;
                });

                if (t > rule.rule) displayedInterventions.push(rule.risk);
            });

            return displayedInterventions;
        },
        fields() {
            const fields = [];

            this.displayedSections.forEach(section => {
                section.fields.forEach(field => {
                    fields.push({ fieldID: field.key, value: field.value });
                });
            });

            return fields;
        },
        results() {
            return fieldID => this.fields.find(field => field.fieldID === fieldID).value;
        },
    },
    watch: {
        fields: {
            handler() {
                this.riskAnalysis.fields = this.fields;
                this.updateData();
            },
            immediate: true,
        },
    },
    created() {
        this.network.name = this.$attrs.name;
        this.network.type = this.$attrs.type;
        this.riskAnalysis.network = this.$attrs.network;
        if (this.$attrs.component) {
            this.network.name = this.$attrs.componentName;
            this.network.type = this.$attrs.componentType;
            this.riskAnalysis.component = this.$attrs.component;
        }

        if (this.mutableSections.length) {
            this.displayedSections = this.displayedSections.map((section, i) => ({
                ...section,
                fields: this.transformFields(i, section.fields),
            }));
        }
    },
    methods: {
        updateData() {
            this.$emit('sendData', this.riskAnalysis);
        },
        transformFields(i, fields) {
            return fields.map((field, j) => ({
                ...field,
                value: this.mutableSections[i].fields[j].value,
            }));
        },
    },
};
</script>
